import { startOfDay } from "date-fns";
import { useTheme } from "styled-components";

import {
  buildWorkspacesMapLayer,
  getRefreshBucketStart,
  getRefreshBucketWindow,
  HaNContext,
  HaNMapStageContext,
  useFacilityMarkers,
  useHaNMapLayer,
} from "@lib/logic";
import {
  getNodeInformationPerAreaByBuildingId,
  getWorkspaceOccupancyForBuildingAndFloorId,
  useAppSelector,
} from "@lib/store";
import { useRequiredContext } from "@lib/utils";
import { useUserProfilePics } from "@hooks";
import { toMapViewUser } from "@utils";

export const WorkspacesMap = () => {
  const { tab } = useRequiredContext(HaNContext);
  const { floorId } = useRequiredContext(HaNMapStageContext);

  if (tab !== "workspaces" || !floorId) return null;

  return <WorkspacesMapLayer floorId={floorId} />;
};

type WorkspacesMapLayerProps = {
  floorId: string;
};

// Builds the tab's layer for the page's one map, which the map stage draws
const WorkspacesMapLayer = ({ floorId }: WorkspacesMapLayerProps) => {
  const theme = useTheme();
  const { bucket, building, filterData, people } = useRequiredContext(HaNContext);
  const buildingId = building.buildingId ?? "";

  const today = startOfDay(new Date(getRefreshBucketStart(bucket)));

  const sensorData = useAppSelector(state => getWorkspaceOccupancyForBuildingAndFloorId(state, buildingId, floorId));
  const areaBookingStates = useAppSelector(state => {
    return getNodeInformationPerAreaByBuildingId(state, today.toISOString(), buildingId);
  });
  const facilityMarkers = useFacilityMarkers(buildingId, floorId);

  // Only the floor's people with a desk, and only those with a picture
  const profilePictures = useUserProfilePics(
    people.reservations
      .filter(({ workspace }) => workspace.floorId === floorId && workspace.deskId)
      .map(({ owner }) => ({ userId: owner.id, imageHash: owner.imageHash ?? "" })),
  );

  const layer = buildWorkspacesMapLayer({
    floorId,
    areas: filterData.workspaces.filteredData,
    sensorData,
    areaBookingStates,
    reservations: people.reservations,
    bucket: getRefreshBucketWindow(bucket),
    toAvatar: owner => {
      return toMapViewUser(
        { email: owner.email, initials: owner.initials ?? "", imageUrl: profilePictures[owner.id] },
        theme,
      );
    },
    facilityMarkers,
  });

  useHaNMapLayer(layer);

  return null;
};
